import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload, FiBook, FiCalendar, FiUser, FiAlertCircle, FiPlus, FiEdit2, FiTrash2, FiX, FiPrinter } from 'react-icons/fi';
import './KRS_Modern.css';

const KRS = () => {
  const [krsList, setKrsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('nim'); // 'nim' or 'nama'
  const [selectedSemester, setSelectedSemester] = useState('');
  const [availableSemesters, setAvailableSemesters] = useState([]); // Add semester list from database
  const [hasSearched, setHasSearched] = useState(false);
  
  // Print by year states
  const [showPrintByYear, setShowPrintByYear] = useState(false);
  const [printYear, setPrintYear] = useState('');
  const [printSemester, setPrintSemester] = useState('');
  const [printProdi, setPrintProdi] = useState('all');
  const [prodiList, setProdiList] = useState([]);
  
  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentKRS, setCurrentKRS] = useState(null);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [formData, setFormData] = useState({
    nim: '',
    kode_mata_kuliah: '',
    id_semester: '',
    nama_kelas_kuliah: 'A',
    status_sync: 'waiting'
  });
  
  const [stats, setStats] = useState({
    total: 0,
    totalSKS: 0
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning('Masukkan NIM atau Nama Mahasiswa');
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    try {
      const params = {
        [searchType]: searchQuery.trim(),
        limit: 1000 // Get all KRS for the student
      };
      
      if (selectedSemester) {
        params.semester = selectedSemester;
      }
      
      const res = await axios.get('/api/krs', { params });
      const data = res.data.data || [];
      
      setKrsList(data);
      
      // Calculate stats
      const totalSKS = data.reduce((sum, krs) => sum + (krs.sks_mata_kuliah || 0), 0);
      
      setStats({
        total: data.length,
        totalSKS
      });
      
      if (data.length === 0) {
        toast.info('Tidak ada data KRS ditemukan');
      } else {
        toast.success(`Ditemukan ${data.length} mata kuliah`);
      }
    } catch (error) {
      toast.error('Gagal mengambil data KRS');
      console.error('Error fetching KRS:', error);
      setKrsList([]);
      setStats({ total: 0, totalSKS: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setSearchQuery('');
    setSelectedSemester('');
    setKrsList([]);
    setHasSearched(false);
    setStats({ total: 0, totalSKS: 0 });
  };

  // Fetch available courses when form opens
  useEffect(() => {
    if (showForm && !editMode) {
      fetchAvailableCourses();
    }
  }, [showForm, editMode]);

  // Fetch list of program studi on mount
  useEffect(() => {
    fetchProdiList();
  }, []);

  const fetchProdiList = async () => {
    try {
      const res = await axios.get('/api/students/stats');
      if (res.data.success && res.data.data.topStudentsByProgram) {
        const prodiNames = res.data.data.topStudentsByProgram.map(p => p._id).filter(Boolean);
        setProdiList(prodiNames);
      }
    } catch (error) {
      console.error('Error fetching prodi list:', error);
    }
  };

  const fetchAvailableCourses = async () => {
    try {
      const res = await axios.get('/api/krs/courses');
      setAvailableCourses(res.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Gagal mengambil data mata kuliah');
    }
  };

  const handleAddKRS = () => {
    setEditMode(false);
    setCurrentKRS(null);
    setFormData({
      nim: searchQuery && searchType === 'nim' ? searchQuery : '',
      kode_mata_kuliah: '',
      id_semester: selectedSemester || '',
      nama_kelas_kuliah: 'A',
      status_sync: 'waiting'
    });
    setShowForm(true);
  };

  const handleEditKRS = (krs) => {
    setEditMode(true);
    setCurrentKRS(krs);
    setFormData({
      nama_kelas_kuliah: krs.nama_kelas_kuliah || 'A',
      status_sync: krs.status_sync || 'waiting',
      nilai_angka: krs.nilai_angka || '',
      nilai_huruf: krs.nilai_huruf || ''
    });
    setShowForm(true);
  };

  const handleDeleteKRS = async (krsId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus KRS ini?')) {
      return;
    }

    try {
      await axios.delete(`/api/krs/${krsId}`);
      toast.success('KRS berhasil dihapus');
      handleSearch(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menghapus KRS');
      console.error('Error deleting KRS:', error);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editMode) {
        // Update KRS
        await axios.put(`/api/krs/${currentKRS._id}`, formData);
        toast.success('KRS berhasil diupdate');
      } else {
        // Create KRS
        await axios.post('/api/krs', formData);
        toast.success('KRS berhasil ditambahkan');
      }
      
      setShowForm(false);
      handleSearch(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gagal menyimpan KRS');
      console.error('Error saving KRS:', error);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePrintByYear = () => {
    if (!printYear || !printSemester) {
      toast.warning('Masukkan tahun pendaftaran dan semester');
      return;
    }

    // Open print window - use full backend URL with prodi filter
    let printUrl = `http://localhost:5000/api/krs/print-by-year/${printYear}/${printSemester}`;
    if (printProdi && printProdi !== 'all') {
      printUrl += `?prodi=${encodeURIComponent(printProdi)}`;
    }
    window.open(printUrl, '_blank');
    toast.success('Membuka halaman cetak...');
  };

  const handlePrintBySemester = () => {
    if (!searchQuery || searchType !== 'nim') {
      toast.warning('Silakan cari mahasiswa berdasarkan NIM terlebih dahulu');
      return;
    }

    if (!selectedSemester) {
      toast.warning('Pilih semester untuk dicetak');
      return;
    }

    // Open print page in new window - must use full backend URL for window.open
    const printUrl = `http://localhost:5000/api/krs/print/${searchQuery}/${selectedSemester}`;
    window.open(printUrl, '_blank');
  };



  const handleExport = async () => {
    if (krsList.length === 0) {
      toast.warning('Tidak ada data untuk diexport');
      return;
    }

    try {
      const params = {
        [searchType]: searchQuery.trim()
      };
      
      if (selectedSemester) {
        params.semester = selectedSemester;
      }
      
      const res = await axios.get('/api/krs/export', { 
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `krs_${searchQuery}_${new Date().getTime()}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Data KRS berhasil diexport');
    } catch (error) {
      toast.error('Gagal export data KRS');
      console.error('Error exporting KRS:', error);
    }
  };

  const getSemesterName = (semesterId) => {
    if (!semesterId) return '-';
    const year = semesterId.toString().substring(0, 4);
    const sem = semesterId.toString().substring(4);
    const nextYear = (parseInt(year) + 1).toString();
    const semName = sem === '1' ? 'Ganjil' : sem === '2' ? 'Genap' : 'Pendek';
    return `${year}/${nextYear} ${semName}`;
  };

  return (
    <div className="krs-page">
      {/* Modern Transparent Header */}
      <div className="page-header modern-header">
        <div className="header-background"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="header-icon-wrapper">
              <FiCalendar className="header-icon" />
            </div>
            <div className="header-text">
              <h1 className="header-title">Kartu Rencana Studi (KRS)</h1>
              <p className="header-subtitle">Cari dan kelola KRS mahasiswa</p>
            </div>
          </div>
          <div className="header-actions">
            {krsList.length > 0 && (
              <>
                <button className="btn btn-glass btn-success" onClick={handleAddKRS}>
                  <FiPlus /> <span className="btn-text">Tambah KRS</span>
                </button>
                <button className="btn btn-glass btn-primary" onClick={handleExport}>
                  <FiDownload /> <span className="btn-text">Export CSV</span>
                </button>
              </>
            )}
            <button 
              className="btn btn-glass btn-secondary" 
              onClick={() => setShowPrintByYear(!showPrintByYear)}
            >
              <FiPrinter /> <span className="btn-text">Cetak by Tahun</span>
            </button>
          </div>
        </div>
      </div>

      {/* Print by Year Panel */}
      {showPrintByYear && (
        <div className="card print-by-year-panel">
          <div className="card-header">
            <h3><FiPrinter /> Cetak KRS Berdasarkan Tahun Pendaftaran</h3>
            <button className="btn btn-outline btn-sm" onClick={() => setShowPrintByYear(false)}>
              <FiX /> Tutup
            </button>
          </div>
          
          <div className="print-by-year-content">
            <div className="form-row">
              <div className="form-group">
                <label>Tahun Pendaftaran</label>
                <input
                  type="number"
                  value={printYear}
                  onChange={(e) => setPrintYear(e.target.value)}
                  placeholder="Contoh: 2024"
                  className="form-control"
                  min="2000"
                  max={new Date().getFullYear()}
                />
                <small className="text-muted">Masukkan tahun mahasiswa masuk (enrollment year)</small>
              </div>

              <div className="form-group">
                <label>Semester</label>
                <input
                  type="text"
                  value={printSemester}
                  onChange={(e) => setPrintSemester(e.target.value)}
                  placeholder="Contoh: 20251"
                  className="form-control"
                />
                <small className="text-muted">Format: YYYYS (20251 = 2025/2026 Ganjil)</small>
              </div>

              <div className="form-group">
                <label>Program Studi</label>
                <select
                  value={printProdi}
                  onChange={(e) => setPrintProdi(e.target.value)}
                  className="form-control"
                >
                  <option value="all">Semua Program Studi</option>
                  {prodiList.map((prodi, index) => (
                    <option key={index} value={prodi}>{prodi}</option>
                  ))}
                </select>
                <small className="text-muted">Filter berdasarkan program studi</small>
              </div>

              <div className="form-group align-end">
                <button 
                  className="btn btn-print btn-lg" 
                  onClick={handlePrintByYear}
                  disabled={!printYear || !printSemester}
                >
                  <FiPrinter /> Cetak Semua KRS
                </button>
              </div>
            </div>

            <div className="info-box">
              <FiAlertCircle />
              <div>
                <strong>Informasi:</strong>
                <p>Fitur ini akan mencetak KRS semua mahasiswa dari tahun pendaftaran yang dipilih untuk semester tertentu.</p>
                <p>Setiap mahasiswa akan dicetak pada halaman terpisah.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search Panel */}
      <div className="card">
        <div className="card-header">
          <h3><FiSearch /> Cari KRS Mahasiswa</h3>
          {hasSearched && (
            <button className="btn btn-outline btn-sm" onClick={handleReset}>
              Reset Pencarian
            </button>
          )}
        </div>
        
        <div className="search-panel">
          <div className="search-controls">
            <div className="form-group">
              <label>Cari Berdasarkan</label>
              <select 
                className="form-control"
                value={searchType}
                onChange={(e) => setSearchType(e.target.value)}
              >
                <option value="nim">NIM</option>
                <option value="nama_mahasiswa">Nama Mahasiswa</option>
              </select>
            </div>

            <div className="form-group flex-grow">
              <label>{searchType === 'nim' ? 'Nomor Induk Mahasiswa (NIM)' : 'Nama Mahasiswa'}</label>
              <div className="input-with-icon">
                <FiSearch />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={searchType === 'nim' ? 'Masukkan NIM mahasiswa...' : 'Masukkan nama mahasiswa...'}
                  className="form-control"
                  autoFocus
                />
              </div>
            </div>

            <div className="form-group">
              <label>Filter Semester (Opsional)</label>
              <input
                type="text"
                value={selectedSemester}
                onChange={(e) => setSelectedSemester(e.target.value)}
                placeholder="Contoh: 20251"
                className="form-control"
              />
            </div>

            <div className="form-group align-end">
              <button 
                className="btn btn-primary btn-search" 
                onClick={handleSearch}
                disabled={loading}
              >
                <FiSearch /> {loading ? 'Mencari...' : 'Cari KRS'}
              </button>
            </div>
          </div>

          <div className="search-info">
            <FiAlertCircle />
            <p>Masukkan NIM atau Nama mahasiswa untuk melihat daftar KRS yang telah disinkronkan dari NEO Feeder</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards - Only show if has data */}
      {krsList.length > 0 && (
        <>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#4f46e5' }}>
                <FiBook />
              </div>
              <div className="stat-details">
                <h3>{stats.total}</h3>
                <p>Total Mata Kuliah</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#10b981' }}>
                <FiCalendar />
              </div>
              <div className="stat-details">
                <h3>{stats.totalSKS}</h3>
                <p>Total SKS</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon" style={{ background: '#f59e0b' }}>
                <FiUser />
              </div>
              <div className="stat-details">
                <h3>{krsList.length > 0 ? krsList[0].nama_mahasiswa || '-' : '-'}</h3>
                <p>Nama Mahasiswa</p>
              </div>
            </div>
          </div>

          {/* Print by Semester Section */}
          {searchType === 'nim' && selectedSemester && (
            <div className="print-section">
              <div className="print-info-box">
                <FiPrinter size={24} />
                <div>
                  <h4>Cetak KRS Semester</h4>
                  <p>Cetak KRS untuk semester {getSemesterName(selectedSemester)}</p>
                </div>
                <button className="btn btn-print" onClick={handlePrintBySemester}>
                  <FiPrinter /> Cetak KRS
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* KRS Table - Only show if searched */}
      {hasSearched && (
        <div className="card">
          <div className="card-header">
            <h3>Daftar KRS</h3>
            {krsList.length > 0 && (
              <span className="text-muted">
                Ditemukan {krsList.length} mata kuliah
              </span>
            )}
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Memuat data KRS...</p>
            </div>
          ) : krsList.length === 0 ? (
            <div className="empty-state">
              <FiBook size={48} />
              <p>Tidak ada data KRS ditemukan</p>
              <small>
                {searchType === 'nim' 
                  ? `Tidak ada KRS untuk NIM "${searchQuery}"`
                  : `Tidak ada KRS untuk mahasiswa dengan nama "${searchQuery}"`
                }
              </small>
              <small className="mt-2">Pastikan data sudah disinkronkan dari NEO Feeder</small>
            </div>
          ) : (
            <div className="modern-table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Kode MK</th>
                    <th>Mata Kuliah</th>
                    <th>Kelas</th>
                    <th>SKS</th>
                    <th>Semester</th>
                    <th>Prodi</th>
                    <th>Status</th>
                    <th>Nilai</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {krsList.map((krs, index) => (
                    <tr key={krs._id || index}>
                      <td>{index + 1}</td>
                      <td><code>{krs.kode_mata_kuliah || '-'}</code></td>
                      <td><strong>{krs.nama_mata_kuliah || '-'}</strong></td>
                      <td>{krs.nama_kelas_kuliah || '-'}</td>
                      <td className="text-center">
                        <span className="badge badge-info">{krs.sks_mata_kuliah || 0}</span>
                      </td>
                      <td>
                        <small>{getSemesterName(krs.id_semester)}</small>
                      </td>
                      <td>
                        <small>{krs.nama_program_studi || '-'}</small>
                      </td>
                      <td>
                        <span className={`badge ${
                          krs.status_sync === 'approved' ? 'badge-success' : 
                          krs.status_sync === 'waiting' ? 'badge-warning' : 
                          'badge-danger'
                        }`}>
                          {krs.status_sync || '-'}
                        </span>
                      </td>
                      <td className="text-center">
                        {krs.nilai_huruf ? (
                          <span className="badge badge-primary">{krs.nilai_huruf}</span>
                        ) : krs.nilai_angka ? (
                          <span className="text-muted">{krs.nilai_angka}</span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-icon btn-edit"
                            onClick={() => handleEditKRS(krs)}
                            title="Edit KRS"
                          >
                            <FiEdit2 />
                          </button>
                          <button 
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteKRS(krs._id)}
                            title="Hapus KRS"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* KRS Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit KRS' : 'Tambah KRS Baru'}</h3>
              <button className="btn-close" onClick={() => setShowForm(false)}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="modal-body">
                {editMode ? (
                  // Edit mode - limited fields
                  <>
                    <div className="form-group">
                      <label>Nama Kelas</label>
                      <input
                        type="text"
                        name="nama_kelas_kuliah"
                        value={formData.nama_kelas_kuliah}
                        onChange={handleFormChange}
                        className="form-control"
                        placeholder="Contoh: A, B, C"
                      />
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status_sync"
                        value={formData.status_sync}
                        onChange={handleFormChange}
                        className="form-control"
                        required
                      >
                        <option value="waiting">Waiting</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Nilai Angka</label>
                        <input
                          type="number"
                          name="nilai_angka"
                          value={formData.nilai_angka}
                          onChange={handleFormChange}
                          className="form-control"
                          placeholder="0-100"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>

                      <div className="form-group">
                        <label>Nilai Huruf</label>
                        <select
                          name="nilai_huruf"
                          value={formData.nilai_huruf}
                          onChange={handleFormChange}
                          className="form-control"
                        >
                          <option value="">- Pilih -</option>
                          <option value="A">A</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B">B</option>
                          <option value="B-">B-</option>
                          <option value="C+">C+</option>
                          <option value="C">C</option>
                          <option value="C-">C-</option>
                          <option value="D">D</option>
                          <option value="E">E</option>
                        </select>
                      </div>
                    </div>
                  </>
                ) : (
                  // Add mode - all fields
                  <>
                    <div className="form-group">
                      <label>NIM Mahasiswa <span className="required">*</span></label>
                      <input
                        type="text"
                        name="nim"
                        value={formData.nim}
                        onChange={handleFormChange}
                        className="form-control"
                        placeholder="Masukkan NIM mahasiswa"
                        required
                      />
                      <small className="text-muted">Mahasiswa harus sudah terdaftar di sistem</small>
                    </div>

                    <div className="form-group">
                      <label>Mata Kuliah <span className="required">*</span></label>
                      <select
                        name="kode_mata_kuliah"
                        value={formData.kode_mata_kuliah}
                        onChange={handleFormChange}
                        className="form-control"
                        required
                      >
                        <option value="">- Pilih Mata Kuliah -</option>
                        {availableCourses.map(course => (
                          <option key={course._id} value={course.kode_mata_kuliah}>
                            {course.kode_mata_kuliah} - {course.nama_mata_kuliah} ({course.sks_mata_kuliah} SKS)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label>Semester <span className="required">*</span></label>
                        <input
                          type="text"
                          name="id_semester"
                          value={formData.id_semester}
                          onChange={handleFormChange}
                          className="form-control"
                          placeholder="Contoh: 20251"
                          required
                        />
                        <small className="text-muted">Format: YYYYS (20251 = 2025/2026 Ganjil)</small>
                      </div>

                      <div className="form-group">
                        <label>Nama Kelas</label>
                        <input
                          type="text"
                          name="nama_kelas_kuliah"
                          value={formData.nama_kelas_kuliah}
                          onChange={handleFormChange}
                          className="form-control"
                          placeholder="Contoh: A, B, C"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status_sync"
                        value={formData.status_sync}
                        onChange={handleFormChange}
                        className="form-control"
                      >
                        <option value="waiting">Waiting</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowForm(false)}>
                  Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  {editMode ? 'Update KRS' : 'Tambah KRS'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default KRS;
